import { useState, useEffect, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useTenantNavigation } from "@/hooks/useTenantNavigation";
import { useOffline } from "@/contexts/OfflineContext";
import { useTenant } from "@/contexts/TenantContext";
import api from "@/lib/api";
import {
  cacheLookups, getCachedLookups, cacheReportsList, getCachedReportsList,
  saveOfflineReport, getOfflineReports, generateOfflineId,
} from "@/lib/offlineStorage";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { format, subDays } from "date-fns";
import { CalendarIcon, LogOut, Plus, FileText, ChevronRight, Unlock, Search, AlertCircle, X } from "lucide-react";
import { Logo } from "@/components/Logo";
import { NotificationBell } from "@/components/NotificationBell";

export default function HomePage() {
  const { user, logout } = useAuth();
  const { navigate } = useTenantNavigation();
  const { isOnline, enqueue } = useOffline();
  const { tenant } = useTenant();
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState("");
  const [selectedShift, setSelectedShift] = useState("");
  const [loading, setLoading] = useState(false);
  const [allReports, setAllReports] = useState([]);
  const [showAll, setShowAll] = useState(false);

  // Filters for the Recent Reports panel
  const [searchTerm, setSearchTerm] = useState("");
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const backdateWindow = tenant?.backdate_window;
  const policy = backdateWindow?.policy || (tenant?.report_date_unlocked ? "open" : "daily");
  const todayStr = format(new Date(), "yyyy-MM-dd");
  // Prefer server-computed dates; fall back to client-side for resilience
  const maxDate = backdateWindow?.max_date || todayStr;
  const minDate = backdateWindow?.min_date || (policy === "open" ? format(subDays(new Date(), tenant?.report_date_days_back || 90), "yyyy-MM-dd") : todayStr);
  const isBackdateAllowed = minDate < maxDate;
  const [selectedDate, setSelectedDate] = useState(maxDate);

  // Sync default selectedDate whenever the tenant config (and thus the window) changes
  useEffect(() => {
    setSelectedDate(maxDate);
  }, [maxDate]);

  useEffect(() => {
    const load = async () => {
      try {
        const [lookups, reports] = await Promise.all([
          api.get("/lookups"),
          api.get("/reports"),
        ]);
        setSites(lookups.sites || []);
        setAllReports(reports || []);
        // Cache for offline
        cacheLookups(lookups);
        cacheReportsList(reports || []);
      } catch {
        // Offline fallback
        const [cachedLookups, cachedReports, offlineReports] = await Promise.all([
          getCachedLookups(),
          getCachedReportsList(),
          getOfflineReports(),
        ]);
        if (cachedLookups) setSites(cachedLookups.sites || []);
        const combined = [...(offlineReports || []), ...(cachedReports || [])];
        setAllReports(combined);
        if (cachedLookups) toast.info("Loaded from offline cache");
        else toast.error("No connection and no cached data");
      }
    };
    load();
  }, []);

  // Build a site name lookup for the report rows
  const siteNameMap = useMemo(() => {
    const m = {};
    sites.forEach((s) => { m[s.id] = s.name; });
    return m;
  }, [sites]);

  // Drafts + Saved are the "unfinished" reports the contractor should never miss
  const unfinishedCount = useMemo(
    () => allReports.filter((r) => r.status === "Draft" || r.status === "Saved").length,
    [allReports]
  );

  const isUnfinished = (r) => r.status === "Draft" || r.status === "Saved";

  // Apply filters + sort (unfinished first, then by report_date desc, then created_at desc)
  const filteredReports = useMemo(() => {
    let list = [...allReports];
    if (statusFilter === "drafts") list = list.filter(isUnfinished);
    else if (statusFilter === "submitted") list = list.filter((r) => r.status === "Submitted");
    if (filterFrom) list = list.filter((r) => (r.report_date || "") >= filterFrom);
    if (filterTo) list = list.filter((r) => (r.report_date || "") <= filterTo);
    if (searchTerm.trim()) {
      const q = searchTerm.trim().toLowerCase();
      list = list.filter((r) => {
        const site = (siteNameMap[r.site_id] || "").toLowerCase();
        return (
          (r.report_date || "").includes(q) ||
          (r.shift || "").toLowerCase().includes(q) ||
          (r.status || "").toLowerCase().includes(q) ||
          site.includes(q)
        );
      });
    }
    list.sort((a, b) => {
      const aU = isUnfinished(a) ? 0 : 1;
      const bU = isUnfinished(b) ? 0 : 1;
      if (aU !== bU) return aU - bU;
      if ((b.report_date || "") !== (a.report_date || "")) return (b.report_date || "").localeCompare(a.report_date || "");
      return (b.created_at || "").localeCompare(a.created_at || "");
    });
    return list;
  }, [allReports, statusFilter, filterFrom, filterTo, searchTerm, siteNameMap]);

  const hasActiveFilter = !!(searchTerm || filterFrom || filterTo || statusFilter !== "all");
  const visibleReports = hasActiveFilter || showAll ? filteredReports : filteredReports.slice(0, 10);

  const clearFilters = () => {
    setSearchTerm(""); setFilterFrom(""); setFilterTo(""); setStatusFilter("all"); setShowAll(false);
  };

  const handleCreateResume = async () => {
    if (!selectedSite || !selectedShift) {
      toast.error("Select site and shift");
      return;
    }
    setLoading(true);
    try {
      if (isOnline) {
        // Get GPS coordinates for geofence check
        let latitude = null, longitude = null;
        try {
          const pos = await new Promise((resolve, reject) =>
            navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 10000, enableHighAccuracy: true })
          );
          latitude = pos.coords.latitude;
          longitude = pos.coords.longitude;
        } catch (geoErr) {
          // GPS unavailable — proceed without coordinates
        }
        const res = await api.post("/reports", {
          report_date: selectedDate,
          site_id: selectedSite,
          shift: selectedShift,
          latitude,
          longitude,
        });
        const report = res.report;
        toast.success("New report created");
        navigate(`/report/${report.id}/header`);
      } else {
        // Offline: create local report placeholder
        const offlineId = generateOfflineId();
        const offlineReport = {
          id: offlineId,
          report_date: selectedDate,
          site_id: selectedSite,
          shift: selectedShift,
          status: "Draft",
          _offline: true,
          created_at: new Date().toISOString(),
        };
        await saveOfflineReport(offlineReport);
        await enqueue({
          id: generateOfflineId(),
          type: "create_report",
          reportId: offlineId,
          payload: {
            report_date: selectedDate,
            site_id: selectedSite,
            shift: selectedShift,
          },
        });
        toast.success("Report created offline — will sync when connected");
        navigate(`/report/${offlineId}/quick-log`);
      }
    } catch (err) {
      const detail = err.response?.data?.detail || "Failed to create report";
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const statusColor = (status) => {
    if (status === "Submitted") return "bg-primary/20 text-primary border-primary/30";
    if (status === "Saved") return "bg-blue-500/20 text-blue-400 border-blue-500/30";
    if (status === "Rejected") return "bg-red-500/20 text-red-400 border-red-500/30";
    return "bg-yellow-500/20 text-yellow-400 border-yellow-500/30";
  };

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Top Bar */}
      <header className="border-b border-border px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Logo />
        </div>
        <div className="flex items-center gap-4">
          <NotificationBell />
          <span className="text-sm text-muted-foreground hidden sm:block">{user?.name || user?.email}</span>
          <Button variant="ghost" size="icon" onClick={logout} data-testid="logout-button">
            <LogOut className="h-4 w-4" strokeWidth={1.5} />
          </Button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-10">
        <h1 className="font-headline text-3xl font-bold tracking-tight mb-2">Daily Report</h1>
        <p className="text-muted-foreground text-sm mb-6">Create or resume a daily contractor report</p>

        {/* Monthly catch-up banner — OPEN window */}
        {policy === "monthly_catchup" && backdateWindow?.in_catchup_window && (
          <Card className="border-emerald-500/40 bg-emerald-500/5 mb-4" data-testid="catchup-open-banner">
            <CardContent className="py-3 px-4 flex items-start gap-3">
              <CalendarIcon className="h-4 w-4 text-emerald-400 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
              <div className="text-sm leading-snug">
                <div className="font-semibold text-emerald-300">Catch-up window OPEN — {backdateWindow.catchup_month_label}</div>
                <div className="text-emerald-200/80 text-xs mt-0.5">
                  Submit all <span className="font-mono">{backdateWindow.catchup_month_label}</span> daily reports before this window closes.
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Monthly catch-up banner — CLOSED, next window scheduled */}
        {policy === "monthly_catchup" && !backdateWindow?.in_catchup_window && backdateWindow?.next_window_opens_at && (
          <Card className="border-muted-foreground/20 bg-muted/20 mb-4" data-testid="catchup-closed-banner">
            <CardContent className="py-3 px-4 flex items-start gap-3">
              <CalendarIcon className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" strokeWidth={1.5} />
              <div className="text-sm leading-snug">
                <div className="font-semibold">Daily entry only</div>
                <div className="text-muted-foreground text-xs mt-0.5">{backdateWindow.description}</div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Unfinished work banner — keeps Drafts top of mind */}
        {unfinishedCount > 0 && (
          <Card className="border-yellow-500/40 bg-yellow-500/5 mb-6" data-testid="unfinished-banner">
            <CardContent className="py-3 px-4 flex items-center gap-3">
              <AlertCircle className="h-4 w-4 text-yellow-400 flex-shrink-0" strokeWidth={1.5} />
              <div className="flex-1 text-sm">
                <span className="font-semibold text-yellow-300">{unfinishedCount}</span>{" "}
                <span className="text-yellow-200">unfinished report{unfinishedCount > 1 ? "s" : ""} need{unfinishedCount > 1 ? "" : "s"} your attention</span>
              </div>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 text-xs text-yellow-300 hover:text-yellow-200 hover:bg-yellow-500/10"
                onClick={() => { setStatusFilter("drafts"); setShowAll(true); }}
                data-testid="view-drafts-btn"
              >
                View
              </Button>
            </CardContent>
          </Card>
        )}

        <Card className="border-border bg-card mb-8">
          <CardContent className="pt-6 space-y-6">
            {/* Report Date */}
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Report Date</Label>
              {isBackdateAllowed ? (
                <div className="space-y-1.5">
                  <input
                    type="date"
                    value={selectedDate}
                    min={minDate}
                    max={maxDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    data-testid="report-date-picker"
                    className="flex h-10 w-full rounded-md border border-border bg-background px-3 py-2 text-sm font-mono ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring cursor-pointer"
                  />
                  <div className={`flex items-start gap-1.5 ${policy === "monthly_catchup" && backdateWindow?.in_catchup_window ? "text-emerald-400" : "text-amber-400"}`}>
                    <Unlock className="h-3 w-3 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                    <span className="text-xs leading-snug" data-testid="backdate-policy-helper">
                      {backdateWindow?.description || `Backdating enabled — up to ${tenant?.report_date_days_back || 90} days back`}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="flex items-center h-10 w-full rounded-md border border-border bg-background px-3" data-testid="locked-date-display">
                  <CalendarIcon className="mr-2 h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                  <span className="font-mono text-sm">{maxDate}</span>
                  <span className="ml-auto text-xs text-muted-foreground" data-testid="locked-date-reason">
                    {backdateWindow?.description || "Locked to today"}
                  </span>
                </div>
              )}
            </div>

            {/* Site Select */}
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Site</Label>
              <Select value={selectedSite} onValueChange={setSelectedSite}>
                <SelectTrigger data-testid="site-select-trigger" className="bg-background border-border">
                  <SelectValue placeholder="Select site" />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  {sites.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Shift Select */}
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Shift</Label>
              <Select value={selectedShift} onValueChange={setSelectedShift}>
                <SelectTrigger data-testid="shift-select-trigger" className="bg-background border-border">
                  <SelectValue placeholder="Select shift" />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="Day">Day</SelectItem>
                  <SelectItem value="Night">Night</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button
              onClick={handleCreateResume}
              data-testid="create-resume-report-button"
              className="w-full font-headline font-semibold h-11"
              disabled={loading || !selectedSite || !selectedShift}
            >
              <Plus className="h-4 w-4 mr-2" strokeWidth={2} />
              {loading ? "Loading..." : "Create New Daily Report"}
            </Button>
          </CardContent>
        </Card>

        {/* My Reports */}
        {allReports.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-headline text-lg font-semibold">My Reports</h2>
              <span className="text-xs text-muted-foreground" data-testid="reports-count">
                {filteredReports.length} of {allReports.length}
              </span>
            </div>

            {/* Search + Filter bar */}
            <Card className="border-border bg-card/50 mb-3">
              <CardContent className="py-3 px-3 space-y-2">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.5} />
                  <Input
                    type="text"
                    placeholder="Search by date, site, shift, status…"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    data-testid="reports-search-input"
                    className="h-9 pl-8 bg-background border-border text-sm"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  <div className="space-y-1">
                    <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">From</Label>
                    <input
                      type="date"
                      value={filterFrom}
                      onChange={(e) => setFilterFrom(e.target.value)}
                      data-testid="reports-filter-from"
                      className="flex h-8 w-full rounded-md border border-border bg-background px-2 text-xs font-mono"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">To</Label>
                    <input
                      type="date"
                      value={filterTo}
                      onChange={(e) => setFilterTo(e.target.value)}
                      data-testid="reports-filter-to"
                      className="flex h-8 w-full rounded-md border border-border bg-background px-2 text-xs font-mono"
                    />
                  </div>
                  <div className="space-y-1 col-span-2 sm:col-span-1">
                    <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Status</Label>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger data-testid="reports-filter-status" className="h-8 bg-background border-border text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-border">
                        <SelectItem value="all">All statuses</SelectItem>
                        <SelectItem value="drafts">Drafts + Saved only</SelectItem>
                        <SelectItem value="submitted">Submitted only</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {hasActiveFilter && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearFilters}
                    data-testid="reports-clear-filters"
                    className="h-7 text-xs text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-3 w-3 mr-1" strokeWidth={1.5} />
                    Clear filters
                  </Button>
                )}
              </CardContent>
            </Card>

            {visibleReports.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6" data-testid="no-reports-found">
                No reports match these filters.
              </p>
            ) : (
              <div className="space-y-2">
                {visibleReports.map((r) => {
                  const unfinished = isUnfinished(r);
                  return (
                    <Card
                      key={r.id}
                      className={`border-border ${unfinished ? "bg-yellow-500/5 border-yellow-500/30" : "bg-card"} hover:bg-accent/30 transition-colors cursor-pointer`}
                      onClick={() => navigate(r.status === "Submitted" ? `/report/${r.id}/review` : `/report/${r.id}/dashboard`)}
                      data-testid={`recent-report-${r.id}`}
                    >
                      <CardContent className="py-3 px-4 flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <FileText className={`h-4 w-4 flex-shrink-0 ${unfinished ? "text-yellow-400" : "text-muted-foreground"}`} strokeWidth={1.5} />
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-mono text-sm">{r.report_date}</span>
                              <span className="text-muted-foreground text-xs">{r.shift} Shift</span>
                            </div>
                            {r.site_id && siteNameMap[r.site_id] && (
                              <div className="text-xs text-muted-foreground truncate mt-0.5">{siteNameMap[r.site_id]}</div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {unfinished && (
                            <span className="text-[10px] uppercase tracking-wider font-semibold text-yellow-400 hidden sm:inline">Continue</span>
                          )}
                          <Badge variant="outline" className={statusColor(r.status)}>{r.status}</Badge>
                          <ChevronRight className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}

                {!hasActiveFilter && !showAll && filteredReports.length > 10 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowAll(true)}
                    data-testid="show-all-reports-btn"
                    className="w-full mt-2 text-xs text-muted-foreground hover:text-foreground"
                  >
                    Show all {filteredReports.length} reports
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
